import type { CategoriaMercadologica } from "@/api/metas.service";

// Seleção hierárquica das categorias de um responsável. A árvore é carregada
// um nível por vez, mas a seleção grava códigos explícitos: marcar um pai marca
// ele e todos os descendentes; desmarcar um filho tira o pai do conjunto e o
// pai passa a aparecer como parcial. Aqui só fica a conta — quem carrega os
// filhos é a tela.

/** Raiz virtual: o pai das categorias de primeiro nível. */
export const RAIZ = "";

export interface NoCategoria {
  id: string;
  nome: string;
  /** `RAIZ` no primeiro nível. */
  pai: string;
}

export interface ArvoreCategorias {
  nos: Record<string, NoCategoria>;
  /** Só tem entrada para quem já teve os filhos carregados por inteiro. */
  filhos: Record<string, string[]>;
}

export const ARVORE_CATEGORIAS_VAZIA: ArvoreCategorias = { nos: {}, filhos: {} };

/** Só categorias entram no escopo: produto é folha do ERP e fica de fora. */
export function soCategorias(linhas: CategoriaMercadologica[]) {
  return linhas.filter((linha) => !linha.folha);
}

/** Guarda os filhos de `pai` como carregados por inteiro. */
export function incorporarFilhos(arvore: ArvoreCategorias, pai: string, filhos: CategoriaMercadologica[]): ArvoreCategorias {
  const nos = { ...arvore.nos };
  for (const filho of filhos) nos[filho.id] = { id: filho.id, nome: filho.nome, pai };
  return { nos, filhos: { ...arvore.filhos, [pai]: filhos.map((f) => f.id) } };
}

/** Guarda nós soltos (um código salvo e os ancestrais dele) sem declarar nenhum pai como completo. */
export function incorporarNos(
  arvore: ArvoreCategorias,
  linhas: { id: string; nome: string; codigoPai: string | null }[],
): ArvoreCategorias {
  const nos = { ...arvore.nos };
  for (const linha of linhas) nos[linha.id] = { id: linha.id, nome: linha.nome, pai: linha.codigoPai ?? RAIZ };
  return { nos, filhos: arvore.filhos };
}

/**
 * Marca ou desmarca `codigo` e todos os `descendentes` (já carregados por quem
 * chama), e refaz os ancestrais: um ancestral só fica no conjunto enquanto
 * todos os filhos dele estiverem.
 */
export function alternar(
  selecao: ReadonlySet<string>,
  arvore: ArvoreCategorias,
  codigo: string,
  descendentes: string[],
  marcar: boolean,
): Set<string> {
  const proxima = new Set(selecao);
  for (const id of [codigo, ...descendentes]) {
    if (marcar) proxima.add(id);
    else proxima.delete(id);
  }
  let pai = arvore.nos[codigo]?.pai ?? RAIZ;
  while (pai !== RAIZ && arvore.nos[pai]) {
    const irmaos = arvore.filhos[pai];
    // Sem a lista completa dos filhos não dá para afirmar que o pai está cheio.
    if (marcar && irmaos && irmaos.every((id) => proxima.has(id))) proxima.add(pai);
    else proxima.delete(pai);
    pai = arvore.nos[pai].pai;
  }
  return proxima;
}

/** Ancestrais de algum selecionado que não estão selecionados — os "parcialmente marcados". */
export function parciais(selecao: ReadonlySet<string>, arvore: ArvoreCategorias): Set<string> {
  const resultado = new Set<string>();
  for (const id of selecao) {
    let pai = arvore.nos[id]?.pai ?? RAIZ;
    while (pai !== RAIZ && arvore.nos[pai] && !resultado.has(pai)) {
      if (!selecao.has(pai)) resultado.add(pai);
      pai = arvore.nos[pai].pai;
    }
  }
  return resultado;
}
